import { useSortable, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { REPORT_BLOCKS, type ReportBlock, type BlockType } from "@/lib/reports/block-types";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GripVertical, X } from "lucide-react";

interface ReportCanvasProps {
  blocks: ReportBlock[];
  selectedBlockId: string | null;
  onSelectBlock: (id: string) => void;
  onRemoveBlock: (id: string) => void;
}

function SortableBlock({
  block,
  isSelected,
  onSelect,
  onRemove,
}: {
  block: ReportBlock;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) {
  const definition = REPORT_BLOCKS[block.type as BlockType];
  const Icon = definition?.icon;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  if (!definition) {
    return (
      <Card ref={setNodeRef} style={style} className="p-3 border-dashed">
        <p className="text-sm text-muted-foreground">[Unknown block type: {block.type}]</p>
      </Card>
    );
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 transition-colors ${isSelected ? "ring-2 ring-primary border-primary" : "hover:border-primary/50"}`}
      onClick={onSelect}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      {Icon && <Icon className="h-4 w-4 text-muted-foreground shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{definition.name}</p>
        <p className="text-xs text-muted-foreground truncate">{definition.description}</p>
      </div>
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
        <X className="h-3.5 w-3.5" />
      </Button>
    </Card>
  );
}

export function ReportCanvas({ blocks, selectedBlockId, onSelectBlock, onRemoveBlock }: ReportCanvasProps) {
  return (
    <div className="flex-1 overflow-y-auto p-6 bg-muted/30">
      {blocks.length === 0 ? (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <div className="text-center space-y-2">
            <p className="text-sm">Drag blocks from the left panel to build your report.</p>
            <p className="text-xs">Or use a preset template to get started.</p>
          </div>
        </div>
      ) : (
        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2 max-w-2xl mx-auto">
            {blocks.map((block) => (
              <SortableBlock
                key={block.id}
                block={block}
                isSelected={selectedBlockId === block.id}
                onSelect={() => onSelectBlock(block.id)}
                onRemove={() => onRemoveBlock(block.id)}
              />
            ))}
          </div>
        </SortableContext>
      )}
    </div>
  );
}
